import {
  autoUpdate,
  FloatingPortal,
  hide,
  offset as offsetMiddleware,
  OffsetOptions,
  Placement,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles
} from '@floating-ui/react'
import { FC, useState } from 'react'

import { ScreenSize } from '@/app/constants/index'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'

type Props = {
  text: string
  renderActivator: FC
  disabled?: boolean
  placement?: Placement
  offset?: OffsetOptions
  className?: string
  colorText?: string
}

const AppTooltip: FC<Props> = ({
  text,
  renderActivator,
  disabled,
  className,
  colorText,
  placement = 'bottom',
  offset = 4
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { breakpoint } = useCurrentBreakpoint()

  const isMobileScreen = [ScreenSize.Xxs, ScreenSize.Xs].includes(breakpoint)

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [offsetMiddleware(offset), hide()]
  })

  const hover = useHover(context, {
    enabled: !isMobileScreen
  })
  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: 'scale(0.8)'
    }
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <>
      {renderActivator({ ref: refs.setReference, ...getReferenceProps() })}

      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={cn(
              'z-50 rounded-8 bg-white px-10 pb-9 pt-7',
              'font-pro text-grey-100 text-18',
              'shadow-[0px_5px_30px_0px_rgba(0,0,0,0.15)]',
              className && 'not-empty',
              disabled && 'hidden'
            )}
            style={{
              ...styles,
              ...floatingStyles,
              visibility: middlewareData.hide?.referenceHidden
                ? 'hidden'
                : 'visible',
              whiteSpace: 'pre-wrap'
            }}
            {...getFloatingProps()}
          >
            <p className={colorText}>{text}</p>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

export default AppTooltip
